import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import CampoSenha from '../../components/CampoSenha';
import RequisitosDeSenha from '../../components/RequisitosDeSenha';

const RedefinirSenha: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const token = params.get('token') || '';
  const email = params.get('email') || '';

  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [errosCampo, setErrosCampo] = useState<Record<string, string[]>>({});

  const linkInvalido = !token || !email;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (enviando) return;

    setErro('');
    setErrosCampo({});

    if (senha !== confirmacao) {
      setErrosCampo({ password: ['As senhas não conferem.'] });
      return;
    }

    setEnviando(true);
    try {
      await apiService.post('/auth/reset-password', {
        token,
        email,
        password: senha,
        password_confirmation: confirmacao,
      });
      navigate('/login', {
        state: { mensagem: 'Senha redefinida com sucesso. Faça login com a nova senha.' },
      });
    } catch (err: any) {
      const dados = err.response?.data;
      if (dados?.errors) setErrosCampo(dados.errors);
      setErro(dados?.message || 'Não foi possível redefinir a senha.');
    } finally {
      setEnviando(false);
    }
  };

  if (linkInvalido) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full space-y-6 bg-white p-8 rounded-lg shadow">
          <h1 className="text-2xl font-bold text-gray-900">Link inválido</h1>
          <p className="text-gray-600">
            Este link de redefinição está incompleto ou foi digitado manualmente. Solicite um novo.
          </p>
          <Link
            to="/esqueci-senha"
            className="block text-center bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700"
          >
            Solicitar novo link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <form onSubmit={handleSubmit} className="max-w-md w-full space-y-6 bg-white p-8 rounded-lg shadow">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Criar nova senha</h1>
          <p className="text-gray-600 mt-2">
            Definindo a senha de <span className="font-medium">{email}</span>.
          </p>
        </div>

        {erro && (
          <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {erro}
          </div>
        )}

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Nova senha
          </label>
          <CampoSenha
            id="password"
            name="password"
            required
            minLength={8}
            autoComplete="new-password"
            aria-invalid={Boolean(errosCampo.password)}
            aria-describedby={errosCampo.password ? 'erro-password' : undefined}
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
          />
          <p className="mt-2 text-xs font-medium text-gray-700">A senha precisa ter, obrigatoriamente:</p>
          <RequisitosDeSenha senha={senha} />
          {errosCampo.password && (
            <p id="erro-password" role="alert" className="text-red-600 text-sm mt-1">
              {errosCampo.password.join(' ')}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="password_confirmation" className="block text-sm font-medium text-gray-700 mb-1">
            Confirme a nova senha
          </label>
          <CampoSenha
            id="password_confirmation"
            name="password_confirmation"
            required
            minLength={8}
            autoComplete="new-password"
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
          />
        </div>

        <button
          type="submit"
          disabled={enviando}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {enviando ? 'Salvando...' : 'Redefinir senha'}
        </button>

        <Link to="/login" className="block text-center text-blue-600 hover:underline">
          Voltar para o login
        </Link>
      </form>
    </div>
  );
};

export default RedefinirSenha;
